import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ItemType } from '@hero/schema';
import {
  listBayanGoodTypeOptions,
  type BayanGoodTypeNode,
  type BayanGoodTypeOption,
} from '@hero/apps';
import { useAppLogistiGoodTypesApi } from '@hero/api-hooks';

export type UseBayanGoodTypesArgs = {
  /** Narrows to the Bayan branch this Hero item type belongs to. */
  narrowToItemType?: ItemType;
  /** Narrows to the branch a waybill blocker named; wins over the item type. */
  narrowToBranchId?: number;
};

export type UseBayanGoodTypesResult = {
  options: BayanGoodTypeOption[];
  isPending: boolean;
  /** Name of a picked id in the reader's language, or `undefined` if unknown. */
  nameOf: (goodTypeId: number | undefined) => string | undefined;
};

const toResult = (options: BayanGoodTypeOption[], isPending: boolean): UseBayanGoodTypesResult => ({
  options,
  isPending,
  nameOf: id => (id === undefined ? undefined : options.find(option => option.id === id)?.label),
});

/**
 * Bayan's cargo catalogue as picker options, from the snapshot or a list
 * the caller already has. Staff tools use this directly — the live lookup
 * is tied to the signed-in Org's Bayan account.
 */
export const useBayanGoodTypeOptions = (
  args: UseBayanGoodTypesArgs & { nodes?: readonly BayanGoodTypeNode[] } = {},
): UseBayanGoodTypesResult => {
  const { i18n } = useTranslation();
  const { nodes, narrowToItemType, narrowToBranchId } = args;
  const language = i18n.language ?? 'en';
  return useMemo(
    () =>
      toResult(
        listBayanGoodTypeOptions({ nodes, narrowToItemType, narrowToBranchId, language }),
        false,
      ),
    [nodes, narrowToItemType, narrowToBranchId, language],
  );
};

/**
 * The cargo types the signed-in Org may file, live from its Bayan account.
 * Shows the snapshot until the lookup resolves, so a picker is never empty.
 */
export const useBayanGoodTypes = (args: UseBayanGoodTypesArgs = {}): UseBayanGoodTypesResult => {
  // `isLoading`, not `isPending`: the query is disabled until Logisti is
  // connected, and a disabled query stays pending forever.
  const { data, isLoading } = useAppLogistiGoodTypesApi();
  const result = useBayanGoodTypeOptions({ ...args, nodes: data });
  return useMemo(() => ({ ...result, isPending: isLoading }), [result, isLoading]);
};
